<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import Icon from '@/Components/UI/Icon.vue'

const { t } = useI18n()

const open = ref(false)
const data = ref({
  starting_soon:     [],
  overdue_handovers: [],
  overdue_returns:   [],
  maintenance_open:  [],
})

const DISMISS_KEY = 'alerts_dismissed_v1'
const dismissed = ref(new Set(readDismissed()))

function readDismissed() {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]') } catch { return [] }
}
function writeDismissed() {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...dismissed.value])) } catch { /* ignore quota */ }
}
function dismiss(id) {
  dismissed.value.add(id)
  dismissed.value = new Set(dismissed.value)
  writeDismissed()
}
function unmute(id) {
  dismissed.value.delete(id)
  dismissed.value = new Set(dismissed.value)
  writeDismissed()
}
function isMuted(id) { return dismissed.value.has(id) }
function toggleMute(id) { isMuted(id) ? unmute(id) : dismiss(id) }

function muteAll() {
  for (const row of allRows()) dismissed.value.add(row.id)
  dismissed.value = new Set(dismissed.value)
  writeDismissed()
}
function unmuteAll() {
  for (const row of allRows()) dismissed.value.delete(row.id)
  dismissed.value = new Set(dismissed.value)
  writeDismissed()
}
function allRows() {
  return [
    ...data.value.starting_soon,
    ...data.value.overdue_handovers,
    ...data.value.overdue_returns,
    ...data.value.maintenance_open,
  ]
}

const groups = computed(() => ({
  overdue_handovers: data.value.overdue_handovers,
  overdue_returns:   data.value.overdue_returns,
  starting_soon:     data.value.starting_soon,
  maintenance_open:  data.value.maintenance_open,
}))

const countLive = (rows) => rows.filter(r => !dismissed.value.has(r.id)).length
const totalCount = computed(() =>
  countLive(groups.value.overdue_handovers) +
  countLive(groups.value.overdue_returns) +
  countLive(groups.value.starting_soon)
)
const hasAny = computed(() =>
  groups.value.overdue_handovers.length +
  groups.value.overdue_returns.length +
  groups.value.starting_soon.length +
  groups.value.maintenance_open.length > 0
)
const hasAnyUnmuted = computed(() =>
  countLive(groups.value.overdue_handovers) +
  countLive(groups.value.overdue_returns) +
  countLive(groups.value.starting_soon) +
  countLive(groups.value.maintenance_open) > 0
)

let timer = null
let inflight = null

async function fetchAlerts() {
  if (document.hidden) return
  try {
    if (inflight) inflight.abort()
    inflight = new AbortController()
    const res = await fetch('/alerts.json', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: inflight.signal,
    })
    if (!res.ok) return
    data.value = await res.json()
    pruneDismissed()
  } catch (e) {
    if (e.name !== 'AbortError') console.warn('[alerts] fetch failed', e)
  }
}

// Drop dismissed IDs that no longer appear in any group — keeps localStorage tidy
// and means re-dismissing a fresh occurrence of the same rental works.
function pruneDismissed() {
  const live = new Set([
    ...data.value.starting_soon,
    ...data.value.overdue_handovers,
    ...data.value.overdue_returns,
    ...data.value.maintenance_open,
  ].map(r => r.id))
  let changed = false
  for (const id of dismissed.value) {
    if (!live.has(id)) { dismissed.value.delete(id); changed = true }
  }
  if (changed) { dismissed.value = new Set(dismissed.value); writeDismissed() }
}

function startPolling() {
  fetchAlerts()
  clearInterval(timer)
  timer = setInterval(fetchAlerts, 60_000)
}
function stopPolling() { clearInterval(timer); timer = null }

function onVisibility() {
  if (document.hidden) stopPolling()
  else startPolling()
}

const triggerRef = ref(null)
function onDocClick(e) {
  if (!open.value) return
  if (triggerRef.value && triggerRef.value.contains(e.target)) return
  open.value = false
}

onMounted(() => {
  startPolling()
  document.addEventListener('visibilitychange', onVisibility)
  document.addEventListener('mousedown', onDocClick)
})
onUnmounted(() => {
  stopPolling()
  document.removeEventListener('visibilitychange', onVisibility)
  document.removeEventListener('mousedown', onDocClick)
  if (inflight) inflight.abort()
})

watch(open, (val) => { if (val) fetchAlerts() })

// ── Date helpers ──────────────────────────────────────────────────
const RTF = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
function timeAgo(iso) {
  if (!iso) return ''
  const ms = Date.parse(iso) - Date.now()
  const abs = Math.abs(ms)
  const m = Math.round(ms / 60000)
  const h = Math.round(ms / 3_600_000)
  const d = Math.round(ms / 86_400_000)
  if (abs < 60_000)   return t('alerts.justNow')
  if (abs < 3_600_000) return RTF.format(m, 'minute')
  if (abs < 86_400_000) return RTF.format(h, 'hour')
  return RTF.format(d, 'day')
}
function formatClock(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })
}
</script>

<template>
  <div ref="triggerRef" class="relative">
    <button
      type="button"
      class="topbar-btn"
      :title="t('alerts.title')"
      :aria-label="t('alerts.title')"
      :aria-expanded="open"
      @click="open = !open"
    >
      <Icon name="bell" :size="16" />
      <span
        v-if="totalCount > 0"
        class="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] fw-value"
        :style="{ background: 'var(--danger)', color: '#fff' }"
      >{{ totalCount > 99 ? '99+' : totalCount }}</span>
    </button>

    <div
      v-if="open"
      class="absolute right-0 mt-2 z-50 w-[360px] max-w-[calc(100vw-1rem)] rounded-[var(--radius-lg)] shadow-[var(--shadow-lg)] border bg-[color:var(--surface)]"
      :style="{ borderColor: 'var(--line)' }"
      role="menu"
    >
      <header class="flex items-center justify-between gap-2 px-4 py-3 border-b" :style="{ borderColor: 'var(--line)' }">
        <h3 class="font-display text-sm text-[color:var(--ink)]">{{ t('alerts.title') }}</h3>
        <div class="flex items-center gap-2">
          <button
            v-if="hasAny"
            type="button"
            class="text-xs text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:underline"
            :title="hasAnyUnmuted ? t('alerts.muteAllHint') : t('alerts.unmuteAllHint')"
            @click="hasAnyUnmuted ? muteAll() : unmuteAll()"
          >{{ hasAnyUnmuted ? t('alerts.muteAll') : t('alerts.unmuteAll') }}</button>
          <button
            type="button"
            class="text-[color:var(--muted)] hover:text-[color:var(--ink)]"
            :aria-label="t('common.close')"
            @click="open = false"
          ><Icon name="x" :size="16" /></button>
        </div>
      </header>

      <div v-if="!hasAny" class="px-4 py-8 text-center text-sm text-[color:var(--muted)]">
        {{ t('alerts.empty') }}
      </div>

      <div v-else class="max-h-[70vh] overflow-y-auto py-1">
        <!-- Overdue handovers -->
        <section v-if="groups.overdue_handovers.length">
          <h4 class="px-4 pt-3 pb-1 text-[10px] tracking-wider uppercase text-[color:var(--muted)]">
            {{ t('alerts.groups.overdueHandover') }}
          </h4>
          <div
            v-for="item in groups.overdue_handovers"
            :key="item.id"
            :class="['px-4 py-2 hover:bg-[color:var(--surfaceAlt)] flex items-start gap-2 transition-opacity', isMuted(item.id) && 'opacity-50']"
          >
            <span class="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" :style="{ background: 'var(--danger)' }" />
            <Link :href="item.href" class="flex-1 min-w-0" @click="open = false">
              <div class="text-sm text-[color:var(--ink)] truncate">{{ item.title || t('alerts.noCustomer') }}</div>
              <div class="text-xs text-[color:var(--muted)] truncate">{{ item.vehicle }}</div>
              <div class="text-xs fw-value" :style="{ color: 'var(--danger)' }">{{ timeAgo(item.time) }} · {{ formatClock(item.time) }}</div>
            </Link>
            <button
              type="button"
              class="text-[color:var(--muted)] hover:text-[color:var(--ink)] shrink-0"
              :aria-label="isMuted(item.id) ? t('alerts.unmute') : t('alerts.mute')"
              :title="isMuted(item.id) ? t('alerts.unmuteHint') : t('alerts.muteHint')"
              @click.stop="toggleMute(item.id)"
            ><Icon :name="isMuted(item.id) ? 'bell' : 'bell-off'" :size="14" /></button>
          </div>
        </section>

        <!-- Overdue returns -->
        <section v-if="groups.overdue_returns.length">
          <h4 class="px-4 pt-3 pb-1 text-[10px] tracking-wider uppercase text-[color:var(--muted)]">
            {{ t('alerts.groups.overdueReturn') }}
          </h4>
          <div
            v-for="item in groups.overdue_returns"
            :key="item.id"
            :class="['px-4 py-2 hover:bg-[color:var(--surfaceAlt)] flex items-start gap-2 transition-opacity', isMuted(item.id) && 'opacity-50']"
          >
            <span class="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" :style="{ background: 'var(--danger)' }" />
            <Link :href="item.href" class="flex-1 min-w-0" @click="open = false">
              <div class="text-sm text-[color:var(--ink)] truncate">{{ item.title || t('alerts.noCustomer') }}</div>
              <div class="text-xs text-[color:var(--muted)] truncate">{{ item.vehicle }}</div>
              <div class="text-xs fw-value" :style="{ color: 'var(--danger)' }">{{ timeAgo(item.time) }} · {{ formatClock(item.time) }}</div>
            </Link>
            <button
              type="button"
              class="text-[color:var(--muted)] hover:text-[color:var(--ink)] shrink-0"
              :aria-label="isMuted(item.id) ? t('alerts.unmute') : t('alerts.mute')"
              :title="isMuted(item.id) ? t('alerts.unmuteHint') : t('alerts.muteHint')"
              @click.stop="toggleMute(item.id)"
            ><Icon :name="isMuted(item.id) ? 'bell' : 'bell-off'" :size="14" /></button>
          </div>
        </section>

        <!-- Starting soon -->
        <section v-if="groups.starting_soon.length">
          <h4 class="px-4 pt-3 pb-1 text-[10px] tracking-wider uppercase text-[color:var(--muted)]">
            {{ t('alerts.groups.startingSoon') }}
          </h4>
          <div
            v-for="item in groups.starting_soon"
            :key="item.id"
            :class="['px-4 py-2 hover:bg-[color:var(--surfaceAlt)] flex items-start gap-2 transition-opacity', isMuted(item.id) && 'opacity-50']"
          >
            <span class="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" :style="{ background: 'var(--warn)' }" />
            <Link :href="item.href" class="flex-1 min-w-0" @click="open = false">
              <div class="text-sm text-[color:var(--ink)] truncate">{{ item.title || t('alerts.noCustomer') }}</div>
              <div class="text-xs text-[color:var(--muted)] truncate">{{ item.vehicle }}</div>
              <div class="text-xs text-[color:var(--muted)]">{{ timeAgo(item.time) }} · {{ formatClock(item.time) }}</div>
            </Link>
            <button
              type="button"
              class="text-[color:var(--muted)] hover:text-[color:var(--ink)] shrink-0"
              :aria-label="isMuted(item.id) ? t('alerts.unmute') : t('alerts.mute')"
              :title="isMuted(item.id) ? t('alerts.unmuteHint') : t('alerts.muteHint')"
              @click.stop="toggleMute(item.id)"
            ><Icon :name="isMuted(item.id) ? 'bell' : 'bell-off'" :size="14" /></button>
          </div>
        </section>

        <!-- Maintenance open -->
        <section v-if="groups.maintenance_open.length">
          <h4 class="px-4 pt-3 pb-1 text-[10px] tracking-wider uppercase text-[color:var(--muted)]">
            {{ t('alerts.groups.maintenanceOpen') }}
          </h4>
          <div
            v-for="item in groups.maintenance_open"
            :key="item.id"
            :class="['px-4 py-2 hover:bg-[color:var(--surfaceAlt)] flex items-start gap-2 transition-opacity', isMuted(item.id) && 'opacity-50']"
          >
            <span class="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" :style="{ background: 'var(--warn)' }" />
            <Link :href="item.href" class="flex-1 min-w-0" @click="open = false">
              <div class="text-sm text-[color:var(--ink)] truncate">{{ item.title }}</div>
              <div v-if="item.subtitle" class="text-xs text-[color:var(--muted)] truncate">{{ item.subtitle }}</div>
              <div class="text-xs text-[color:var(--muted)]">{{ t('alerts.openedAgo', { ago: timeAgo(item.time) }) }}</div>
            </Link>
            <button
              type="button"
              class="text-[color:var(--muted)] hover:text-[color:var(--ink)] shrink-0"
              :aria-label="isMuted(item.id) ? t('alerts.unmute') : t('alerts.mute')"
              :title="isMuted(item.id) ? t('alerts.unmuteHint') : t('alerts.muteHint')"
              @click.stop="toggleMute(item.id)"
            ><Icon :name="isMuted(item.id) ? 'bell' : 'bell-off'" :size="14" /></button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
